import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  DxButtonModule,
  DxDataGridModule,
  DxPopupModule,
  DxSelectBoxModule,
  DxTagBoxModule,
  DxTemplateModule,
  DxTextBoxModule,
} from "devextreme-angular";
import { ContactsAddCompanyInlineComponent } from "../contacts-add-company-inline/contacts-add-company-inline.component";
import { ContactsAddPersonInlineComponent } from "../contacts-add-person-inline/contacts-add-person-inline.component";
import { ContactService } from "src/app/services/contact.service";
import { ConfigService } from "src/app/services/config.service";
import { AppGlobals } from "../../app.globals";
import { ValueChangedEvent } from "devextreme/ui/tag_box";
import { AppModule } from "src/app/app.module";
import { GpdButtonComponent } from "../gpd-button/gpd-button.component";
import { GpdRoleSelectComponent } from "../gpd-role-select/gpd-role-select.component";

import { SearchableDropdownDirective } from '../../directives/searchable-dropdown.directive';
import { Subscription } from 'rxjs';
import { FeatureFlags } from '../../interfaces/enums/feature-flag';
import { SocietyContact, SocietyService } from '../../services/society.service';
import {
  ContactSearchService,
  ContactSearchSession,
  contactSearchLoadingText,
  contactSearchMessage,
} from 'src/app/services/contact-search.service';
type PopupSearchType = 'company' | 'person' | 'society';

/** Tab indexes: the switch in the template and popupSelectedTab use them. */
const SOCIETY_TAB = 2;

@Component({
    selector: "app-contacts-popup",
    imports: [
        CommonModule,
        DxPopupModule,
        DxDataGridModule,
        DxButtonModule,
        DxTextBoxModule,
        ContactsAddCompanyInlineComponent,
        ContactsAddPersonInlineComponent,
        DxTagBoxModule,
        DxTemplateModule,
        GpdButtonComponent,
        GpdRoleSelectComponent,
        SearchableDropdownDirective,
    ],
    templateUrl: "./contacts-popup.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./contacts-popup.component.css"]
})
export class ContactsPopupComponent implements OnInit, OnDestroy {
  @ViewChild("companyGrid") companyGrid;
  @ViewChild("personGrid") personGrid;
  @ViewChild(ContactsAddCompanyInlineComponent) addCompanyForm?: ContactsAddCompanyInlineComponent;
  @ViewChild(ContactsAddPersonInlineComponent) addPersonForm?: ContactsAddPersonInlineComponent;

  @Input("visible") visible = false;
  @Input("contactType") contactType: "company" | "person" | "all" = "all";
  @Input() tagFilter: boolean = false;
  @Input() title?: string;
  @Input() excludeContactId?: number;
  @Input() excludeContactType?: "company" | "person" | "society";
  /**
   * Third tab "Gesellschaften" (contactType 'society'): only where a Gesellschaft fits (the
   * Grundstückseigentümer), and only while the Gesellschaften feature is on.
   */
  @Input() allowSocieties = false;
  readonly societyTab = SOCIETY_TAB;
  societies: SocietyContact[] | null = null;
  private societiesSubscription: Subscription | null = null;
  /** The Gesellschaften there were when "Gesellschaft erstellen" opened its tab; the new one is picked on return. */
  private pendingSocietyIds: Set<number> | null = null;

  get showSocieties(): boolean {
    return this.allowSocieties && this.contactType === 'all' && AppGlobals.UserCan(FeatureFlags.Gesellschaften);
  }
  // Off by default — this popup is used in ~10 places across the app (task assignees, cost
  // entries, property owners, etc.) where "role" has no meaning. Only project-add-contact-modal
  // (the "Kontakt hinzufügen" flow) turns this on, since only there does creating the contact
  // inline also mean immediately assigning it a role for that project.
  @Input() showRole: boolean = false;
  // Bound into the projected role block below (see popup-role in the template) — kept here
  // rather than on the inline form itself since it's this popup's own concern, not the
  // create-person/company form's.
  role: string = '';

  editMode = false;
  openedDirectlyInEditMode = false;
  popupSelectedTab = 0;

  @Input() companies = new Array();
  @Input() persons = new Array();
  contactTags: any;
  selectedTags: any[] = [];

  @Output() onContactSelected = new EventEmitter();
  @Output() onContactAdded = new EventEmitter();
  @Output() onHiding = new EventEmitter();

  searchValueCompany: string;
  filtersCompany: any;

  searchValuePerson: string;
  searchFilterPerson: any;

  tagFilterValues: any;

  /**
   * Firmen and Personen tabs: searched on the server from 2 characters (sound-alikes, typos,
   * titles, legal forms, e-mail, city) instead of the grid's search panel; the grid then shows
   * exactly those hits in the server's order. Shorter input filters the grid as the search
   * panel did. The text stays when switching tabs (each grid is re-created on its tab).
   */
  readonly searches: Record<PopupSearchType, ContactSearchSession>;
  searchText: Record<PopupSearchType, string> = { company: '', person: '', society: '' };
  private grids: Record<PopupSearchType, any> = { company: null, person: null, society: null };
  readonly personRank = (row: any): number => this.searches.person.rankOf('person', row?.id);
  readonly companyRank = (row: any): number => this.searches.company.rankOf('company', row?.id);
  readonly societyRank = (row: any): number => this.searches.society.rankOf('society', row?.id);

  noDataText(type: PopupSearchType): string {
    return contactSearchMessage(this.searches[type]) ?? $localize`:@@noData:No data`;
  }

  // While creating a new contact inline, the header names what's actually being created
  // ("Person erstellen"/"Firma erstellen") instead of staying on the generic picker title.
  get popupTitle(): string {
    if (this.editMode) {
      return this.popupSelectedTab === 1
        ? $localize`:@@createPerson:Person erstellen`
        : $localize`:@@createCompany:Firma erstellen`;
    }
    return this.title || $localize`:@@addContact:Kontakt hinzufügen`;
  }

  constructor(
    private contactService: ContactService,
    private configService: ConfigService,
    contactSearchService: ContactSearchService,
    private societyService: SocietyService
  ) {
    this.searches = {
      company: contactSearchService.createSession(['company'], () => this.applySearch('company'), 50),
      person: contactSearchService.createSession(['person'], () => this.applySearch('person'), 50),
      society: contactSearchService.createSession(['society'], () => this.applySearch('society'), 50),
    };
  }

  ngOnDestroy(): void {
    this.societiesSubscription?.unsubscribe();
    Object.values(this.searches).forEach(s => s.dispose());
  }

  /** A Gesellschaft has its own page: "Gesellschaft erstellen" opens it in a new tab, the popup stays. */
  createSociety(): void {
    this.pendingSocietyIds = new Set((this.societies ?? []).map(s => s.id));
    window.open('contacts/addSociety', '_blank');
  }

  // Back from the "Gesellschaft erstellen" tab: reload and pick the one created there.
  @HostListener('window:focus')
  async onWindowFocus(): Promise<void> {
    const known = this.pendingSocietyIds;
    if (!known || !this.visible) return;
    const list = await this.societyService.loadContacts(true);
    const created = list.filter(s => !known.has(s.id));
    if (created.length !== 1) return; // nothing saved yet (or unclear which): keep waiting
    this.pendingSocietyIds = null;
    this.handleContactSelected({ data: created[0] }, 'society');
  }

  /** The grid is re-created whenever its tab opens again: it starts from the current search. */
  onGridInitialized(type: PopupSearchType, e: any): void {
    this.grids[type] = e.component;
    setTimeout(() => this.applySearch(type));
  }

  onGridDisposing(type: PopupSearchType): void {
    this.grids[type] = null;
  }

  onSearchInput(type: PopupSearchType, value: string): void {
    this.searchText[type] = value ?? '';
    this.searches[type].setQuery(this.searchText[type]);
    // While the search runs the grid shows its loading panel; applySearch takes it away.
    if (this.searches[type].pending) this.grids[type]?.beginCustomLoading(contactSearchLoadingText());
    else this.applySearch(type);
  }

  /**
   * Server hits while a search is active (no second text filter on top), otherwise the grid's own
   * search; the tag filter applies in both cases. The only writer of the grid filter, so search
   * and tags can't overwrite each other.
   */
  private applySearch(type: PopupSearchType): void {
    const grid = this.grids[type];
    const search = this.searches[type];
    // A tag change while a search is on its way: keep the loading panel, the answer applies both.
    if (!grid || search.pending) return;
    grid.endCustomLoading();
    // Gesellschaften have no tags (and no tag box).
    const tags: any[] = type === 'society' ? [] : this.tagFilterValues ?? [];
    const matchesTags = (item: any) => tags.length === 0 || tags.some(tagId => (item?.tags ?? []).includes(tagId));
    if (search.active) {
      grid.searchByText('');
      grid.columnOption('searchRank', { sortOrder: 'asc', sortIndex: 0 });
      grid.filter((item: any) => search.isHit(type, item?.id) && matchesTags(item));
    } else {
      grid.columnOption('searchRank', 'sortOrder', undefined);
      grid.filter(tags.length ? matchesTags : null);
      grid.searchByText(this.searchText[type].trim());
    }
  }

  ngOnInit(): void {
    this.getPageData();
    if (this.allowSocieties) {
      this.societiesSubscription = this.societyService.contacts$.subscribe(list => (this.societies = list));
      this.societyService.loadContacts();
    }
    if (this.contactType == "company") {
      this.popupSelectedTab = 0;
    } else if (this.contactType == "person") {
      this.popupSelectedTab = 1;
    }
  }

  getPageData() {
    this.configService.getContactTags({}).then(
      (result) => {
        if (result.hasError === true)
          AppGlobals.ShowErrorMessage(result.feedback);
        else {
          this.contactTags = result.contactTags;
        }
      },
      (error: any) => {
        AppGlobals.ShowErrorMessage(error);
      }
    );
  }

  handleContactSelected(e, type) {
    if (this.isExcludedContact(e.data, type)) {
      AppGlobals.ShowErrorMessage(
        $localize`:@@ownerAlreadySelected:This contact is already the current property owner.`
      );
      return;
    }
    this.getPageData();
    this.onContactSelected.emit({ data: e.data, type: type });
    this.editMode = false;
    this.visible = false;
  }

  isExcludedContact(data, type): boolean {
    return (
      this.excludeContactId != null &&
      data?.id === this.excludeContactId &&
      type === this.excludeContactType
    );
  }

  onRowPrepared(e, type) {
    if (e.rowType === "data" && this.isExcludedContact(e.data, type)) {
      e.rowElement.classList.add("disabled-row");
    }
  }

  handleNewContactSelected(e, type) {
    this.editMode = false;
    if(type == 'person'){
      this.persons.push(e.data);
    }else if(type == 'company'){
      this.companies.push(e.data);
    }
    this.onContactAdded.emit({
      id: e.data.id,
      type: type,
      displayName: e.data.displayName,
      role: this.role,
    });
    this.role = '';
  }

  // Bound instead of a plain "editMode = true" in the template so a role typed for a
  // previous (aborted or completed) creation doesn't linger into the next one.
  startCreating(): void {
    this.editMode = true;
    this.role = '';
  }
  filterValueChange($event) {}
  filterTags(filterValue, selectedFilterOperation) {
    if (!filterValue || filterValue.length === 0) {
      return null;
    }

    return function (item) {
      if (!item.tags.includes(filterValue)) {
        return false;
      }
      return true;
    };
  }

  handleAbort(e?) {
    if (this.openedDirectlyInEditMode) {
      this.visible = false;
    } else {
      this.editMode = false;
    }
  }

  submitInlineForm() {
    if (this.popupSelectedTab === 0) {
      this.addCompanyForm?.handleSubmitButton(null);
    } else {
      this.addPersonForm?.handleSubmitButton(null);
    }
  }

  handleOnHiding() {
    this.popupSelectedTab = 0;
    this.onHiding.emit();
  }

  onPopupHidden() {
    this.editMode = false;
    this.openedDirectlyInEditMode = false;
    this.pendingSocietyIds = null;
    for (const type of ['company', 'person', 'society'] as PopupSearchType[]) {
      this.searchText[type] = '';
      this.searches[type].setQuery('');
      this.applySearch(type);
    }
  }

  getTag(item) {
    let tag = this.contactTags.find((tag) => tag.id == item);
    if (tag) {
      return tag.title;
    } else {
      return "";
    }
  }

  tagValueChangedCompany(e: ValueChangedEvent) {
    this.selectedTags = e.value;
    this.companyGrid.instance.filter(function (item) {
      for (let tag of e.value) {
        if (!item.tags.includes(tag)) {
          return false;
        }
      }
      return true;
    });
  }
  tagValueChangedPerson(e: ValueChangedEvent) {
    this.selectedTags = e.value;
    this.personGrid.instance.filter(function (item) {
      for (let tag of e.value) {
        if (!item.tags.includes(tag)) {
          return false;
        }
      }
      return true;
    });
  }

  // searchValueChangedPerson(e) {
  //   this.searchValuePerson = e.value;
  //   this.searchFilterPerson = [
  //     ['title', 'contains', this.searchValuePerson],
  //     'or',
  //     ['salutation', 'contains', this.searchValuePerson],
  //     'or',
  //     ['firstname', 'contains', this.searchValuePerson],
  //     'or',
  //     ['lastname', 'contains', this.searchValuePerson],
  //     'or',
  //     ['address', 'contains', this.searchValuePerson],
  //     'or',
  //     ['city', 'contains', this.searchValuePerson],
  //     'or',
  //     ['zipcode', 'contains', this.searchValuePerson]
  //   ];
  //   this.applyFilterPersonGrid();
  // }

  applyFilterCompanyGrid() {
    this.companyGrid.instance.filter([this.filtersCompany]);
  }

  applyFilterPersonGrid() {
    this.personGrid.instance.filter([this.searchFilterPerson]);
  }

  onCellPrepared(e) {
    if (this.searchValueCompany || this.searchFilterPerson) {
      let value = e.displayValue;
      //let titleLower = title.toLowerCase().split(this.searchValue).join(searchMatchString);

      let searchValue;
      if (this.popupSelectedTab == 0) {
        searchValue = this.searchValueCompany;
      }
      if (this.popupSelectedTab == 1) {
        searchValue = this.searchValuePerson;
      }

      let index = value.toLowerCase().indexOf(searchValue.toLowerCase());
      if (index != -1) {
        let match = value.slice(index, index + searchValue.length);
        var searchMatchString =
          "<span class='search-text'>" + match + "</span>";
        value = value.split(match).join(searchMatchString);
        var result = "<div>" + value + "</div>";
        if (e.cellElement) {
          e.cellElement.innerHTML = result;
        }
      }
    }
  }

  /** Contacts with any of the picked tags, combined with the search (applySearch). */
  filterContactsByTag(e: any) {
    this.applySearch('company');
    this.applySearch('person');
  }
}
